import {
  User, MapPin, Mail, Phone, Upload, Send, Loader2, X
} from 'lucide-react';

// ─────────────────────────────────────────
// HEADER BAR
// The top card on the client detail page: avatar, name, contact pills,
// status pills (intake complete / pending / sent), Upload + Send Intake buttons.
// Also renders the resend-email form panel and the upload progress bar
// since they're tightly coupled to the header buttons.
// ─────────────────────────────────────────
export default function HeaderBar({
  client,
  onUploadFile,
  uploadingFile,
  uploadProgress,
  // Resend intake form
  showResendForm,
  setShowResendForm,
  resendEmail,
  setResendEmail,
  resendingIntake,
  resendSuccess,
  onResendIntake,
}) {
  const fullName = `${client.firstName || ''} ${client.lastName || ''}`.trim();
  const initials = `${(client.firstName || '')[0] || ''}${(client.lastName || '')[0] || ''}`.toUpperCase();

  return (
    <>
      {/* Top card: avatar + name + buttons */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-6 py-5 flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold text-lg shadow-md">
            {initials || <User size={22} />}
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">{fullName || 'Unnamed Client'}</h1>
            <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 flex-wrap">
              {client.riskState && <span className="flex items-center gap-1"><MapPin size={11} />{client.riskState}</span>}
              {client.email && <span className="flex items-center gap-1"><Mail size={11} />{client.email}</span>}
              {client.phone && <span className="flex items-center gap-1"><Phone size={11} />{client.phone}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {resendSuccess && (
            <span className="text-xs font-medium text-green-700 bg-green-50 border border-green-200 px-3 py-1.5 rounded-xl">
              ✓ Intake form sent
            </span>
          )}
          {client.intakeCompletedAt && (
            <span className="text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded-xl">
              ✓ Intake complete
            </span>
          )}
          {client.intakeSentAt && !client.intakeCompletedAt && (
            <span className="text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-xl">
              ⏳ Intake pending
            </span>
          )}
          <label className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold border transition cursor-pointer ${uploadingFile ? 'bg-slate-100 text-slate-400 border-slate-200' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}>
            {uploadingFile ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload File
            <input type="file" className="hidden" onChange={onUploadFile} disabled={uploadingFile} />
          </label>
          <button
            onClick={() => setShowResendForm(s => !s)}
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 transition"
          >
            <Send size={13} /> Send Intake Form
          </button>
        </div>
      </div>

      {/* Resend email form panel — appears below the header card when toggled */}
      {showResendForm && (
        <div className="bg-blue-50 border border-blue-200 rounded-2xl px-5 py-4 flex items-end gap-3">
          <div className="flex-1">
            <label className="block text-xs font-medium text-blue-800 mb-1">Send intake form to</label>
            <input
              type="email"
              value={resendEmail}
              onChange={e => setResendEmail(e.target.value)}
              placeholder="client@example.com"
              className="w-full rounded-xl border border-blue-300 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500"
            />
          </div>
          <button
            onClick={onResendIntake}
            disabled={resendingIntake || !resendEmail}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 transition disabled:opacity-50"
          >
            {resendingIntake ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send
          </button>
          <button
            onClick={() => setShowResendForm(false)}
            className="p-2 text-slate-400 hover:text-slate-600"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Upload progress bar — appears while a file is uploading */}
      {uploadingFile && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm px-6 py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">Uploading document...</span>
            <span className="text-sm font-semibold text-blue-600">{uploadProgress}%</span>
          </div>
          <div className="w-full bg-slate-100 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-200"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}
    </>
  );
}